import React from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import { PlatinumTableWrapper } from '../../platinum-table-new/PlatinumTableWrapper'
import type { MarkedCell } from '../../platinum-table-new/PlatinumTable'
import type { PlatinumData } from '../../../types/carbon/models/PlatinumData'

// Shared table-citation modal: opens the referenced table in a full-size
// dialog. Originally lived only in MultiTableAnalysisWidget (Gen AI insights
// "[Table: ...]" citation links) — extracted here so chart widgets can reuse
// the exact same popup for their "View table" link (#1135). No editor-only
// props required, so it works in read-only/shared dashboard contexts too.
export interface TableCitationModalProps {
  open: boolean
  onClose: () => void
  tableName: string
  customer: string
  job: string
  /** The filter the chart / analysis above ran under (#1251). The popup
   *  loads the table under the SAME filter, so it may show more than the
   *  chart (the whole table, no selection) but never different data. */
  filter?: string
  /** The dashboard filter was dropped for this table because it belongs to
   *  a different job than the filter (#1251) — say so rather than let the
   *  reader assume it applied. */
  filterDropped?: boolean
  /** The cell a numbered citation pointed at (#1296), addressed by row and
   *  column LABEL so it survives a table reflow. Matched case-insensitively on
   *  trimmed labels; a cell the table does not have simply is not highlighted,
   *  and the table opens as normal. */
  highlightCell?: { row: string; column: string }
  /** The table itself, when the caller has already loaded it (the low base
   *  notes, #1647). Shown as given, with no second fetch, so the marked
   *  cells index the same table the warning judged. A widget built from a
   *  spec has no table path to load from. */
  data?: PlatinumData
  /** Cells to mark in amber by data index, with one line saying why. */
  markedCells?: MarkedCell[]
  markedNote?: string
}

/** One line under the title declaring the base the table was run on. */
export function citationFilterLine(filter: string | undefined, filterDropped: boolean | undefined): string {
  if (filter) return `Filtered: ${filter}`
  if (filterDropped) return 'Not filtered — this table is from a different job than the dashboard filter'
  return 'No filter applied'
}

export const TableCitationModal: React.FC<TableCitationModalProps> = ({ open, onClose, tableName, customer, job, filter, filterDropped, highlightCell, data, markedCells, markedNote }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="lg"
    fullWidth
    sx={{
      '& .MuiDialog-paper': {
        width: '90%',
        height: '85%',
        maxWidth: 'none',
        maxHeight: 'none',
      },
    }}
  >
    <DialogTitle>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            {tableName || 'Table'}
          </Typography>
          <Typography variant="caption" color={filter ? 'info.main' : 'text.secondary'} data-testid="citation-filter-line">
            {citationFilterLine(filter, filterDropped)}
          </Typography>
          {highlightCell && (
            <Typography variant="caption" color="text.secondary" display="block" data-testid="citation-cell-line">
              Cited cell: {highlightCell.row} x {highlightCell.column}
            </Typography>
          )}
          {markedNote && (
            <Typography variant="caption" color="warning.main" display="block" data-testid="citation-marked-line">
              {markedNote}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
    <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {/* A cross-job agent session carries no job (#1294), and the table
            loader needs one. Say so: without this the dialog opened onto blank
            white space, because the wrapper renders null when it has no data
            and nothing to fetch it with. */}
        {open && data && (
          <PlatinumTableWrapper
            customer={customer}
            job={job}
            data={data}
            markedCells={markedCells}
          />
        )}
        {open && !data && tableName && !job && (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="citation-no-job"
            sx={{ p: 3 }}
          >
            This session is not tied to a job, so this table cannot be opened here.
          </Typography>
        )}
        {open && !data && tableName && job && (
          <PlatinumTableWrapper
            customer={customer}
            job={job}
            tablePath={tableName}
            filter={filter || ''}
            highlightCell={highlightCell}
            markedCells={markedCells}
          />
        )}
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="outlined">
        Close
      </Button>
    </DialogActions>
  </Dialog>
)

export default TableCitationModal
